import React, { FunctionComponent, useState, useEffect } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Tab } from "@own-local/ad-unit/lib/AdUnit";
import { isUrlValid } from "../../shared/validations";

import WizardContainer, { Invocations, SelectedWidget } from "../../containers/Wizard";
import OrigamiPreview from "../OrigamiPreview";
import { dateNormalizer } from "../../shared/normalizers";
import OrderSummary, { ItemProps } from "../OrderSummary";
import CheckouForm from "../CheckoutForm";
import { ORDER_CONFIRMATION_PATH } from "../../routing/paths";
import { ProcessingPayment } from "../ProcessingPayment";

const PAYMENT_COMPLETED_SPELL_NAME = "payment_completed";
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");

export const ORIGAMI_DATA_SPELLS = [
  "business",
  "business_name",
  "caption",
  "background",
  "logo",
  "layout",
  "address",
  "city",
  "zip",
  "business_phone",
  "website",
  "facebook",
  "twitter",
  "instagram",
  "additional_text",
  "email",
  "state",
];

const ORIGAMI_DATA_PREPENDS_MAP: OrigamiDataType = {
  facebook: "https://facebook.com/",
  twitter: "https://twitter.com/",
  instagram: "https://instagram.com/",
};

export type OrigamiDataType = {
  [key: string]: string | object | undefined;
};

type PaymentInformationStepProps = {
  invocations: Invocations;
  changePath: Function;
  invocate: Function;
  submitPaymentIntent: Function;
  updatePaymentIntent: Function;
  setIsProcessingPayment: Function;
  isProcessingPayment: boolean;
};

const defineOrigamiData = (invocations: Invocations): OrigamiDataType => {
  return ORIGAMI_DATA_SPELLS.reduce<OrigamiDataType>(
    (origamiData, spell): OrigamiDataType => {
      const prependValue = ORIGAMI_DATA_PREPENDS_MAP[spell] || "";
      if (invocations[spell]) origamiData[spell] = `${prependValue}${invocations[spell]}`;

      if (spell === "logo") {
        origamiData["logo"] = isUrlValid(invocations["logo"]?.toString())
          ? invocations["logo"]
          : "";
      }

      return origamiData;
    },
    {},
  );
};

const calculateEndDate = (startDate: string, weeks: number) => {
  const totalTime = 1000 * 60 * 60 * 24 * 7 * weeks;
  const endDate = new Date(
    new Date(startDate.replace(/-/g, "/")).getTime() + totalTime,
  ).toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  return endDate;
};

const calculateTotal = (price: number, weeks: number) => {
  return price * weeks;
};

export const PaymentInformationStep: FunctionComponent<PaymentInformationStepProps> = ({
  invocations,
  changePath,
  submitPaymentIntent,
  updatePaymentIntent,
  setIsProcessingPayment,
  isProcessingPayment,
  invocate,
}: PaymentInformationStepProps) => {
  const [origamiData, setOrigamiData] = useState<OrigamiDataType>(
    defineOrigamiData(invocations),
  );

  const selectedWidget = invocations["widget"] as SelectedWidget;
  const selectedDuration = Number(invocations["duration"]);
  const selectedStartDate = String(invocations["start_date"]);
  const selectedEndDate = calculateEndDate(
    String(invocations["start_date"]),
    selectedDuration,
  );
  const paymentIntentSecret = invocations["payment_intent"] as string;

  useEffect(() => {
    setOrigamiData(defineOrigamiData(invocations));
  }, [setOrigamiData, invocations]);

  const totalAmount = selectedWidget
    ? calculateTotal(selectedWidget.price, selectedDuration)
    : null;
  const summaryItems: Array<ItemProps> = [
    {
      klass: "line-rate",
      label: "Rate",
      value: `$${selectedWidget?.price}/week`,
    },
    {
      klass: "line-total-weeks",
      label: "Total Weeks",
      value: selectedDuration,
    },
    {
      klass: "line-start-date",
      label: "Start Date",
      value: dateNormalizer(selectedStartDate),
    },
    {
      klass: "line-end-date",
      label: "End Date",
      value: dateNormalizer(selectedEndDate),
    },
    {
      klass: "line-total",
      label: "Total",
      value: `$${totalAmount?.toFixed(2)}`,
    },
  ];

  const handlePaymentCompleted = (paymentIntentSecret: string) => {
    invocate(PAYMENT_COMPLETED_SPELL_NAME, paymentIntentSecret, ORDER_CONFIRMATION_PATH);
  };

  return (
    <div>
      {isProcessingPayment && <ProcessingPayment />}
      <OrigamiPreview
        spell="payment_information"
        origamiData={origamiData}
        selectedTab={Tab.Info}
        showExpanded={false}
      />
      <div className="question">
        <div className="stripe-form">
          <Elements stripe={stripePromise}>
            <CheckouForm
              changePath={changePath}
              submitPaymentIntent={submitPaymentIntent}
              updatePaymentIntent={updatePaymentIntent}
              setIsProcessingPayment={setIsProcessingPayment}
              paymentIntentSecret={paymentIntentSecret}
              totalAmount={totalAmount}
              handlePaymentCompleted={handlePaymentCompleted}
            >
              <OrderSummary
                title="Order summary"
                description="Check your order details before proceeding"
                items={summaryItems}
                showFooter
                loading={!selectedWidget}
              />
            </CheckouForm>
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default WizardContainer(PaymentInformationStep);
